import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { APIType } from '~/backend/types';
import { Modality } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useRegistration } from '~/hooks/workflows/backend/tasks/useRegistration';
import { useBackendModels } from '~/hooks/workflows/backend/useBackend';
import { primaryImageState } from '~/state/atoms/image/imageAtoms';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import ContourPropagation, {
    RegistrationTargetImage,
} from './ContourPropagation';

export function normalizeBackendImage(
    image?: Image3D
): RegistrationTargetImage {
    return {
        seriesId: image?.seriesUid ?? '',
        modality: image?.seriesMetadata.modality ?? Modality.UNKNOWN,
        label: image?.seriesMetadata.seriesDescription,
    };
}

export default function AdaptBackendWorkflow() {
    const { startProcessing, isSubmitting } = useRegistration();
    const patient = useSelectedPatient();
    const primaryImage = useRecoilValue(primaryImageState);
    const {
        data: availableModels = [],
        isLoading: isLoadingModels,
        error,
    } = useBackendModels(APIType.REGISTRATION);

    const availableImages = useMemo(
        () => patient?.images.filter((image) => image !== primaryImage) || [],
        [primaryImage, patient]
    );

    const [registrationTargetImage, setRegistrationTargetImage] = useState<
        RegistrationTargetImage | undefined
    >(
        availableImages.length > 0
            ? normalizeBackendImage(availableImages[0])
            : undefined
    );

    const onSubmit = (
        selectedModel: PredictionModel,
        selectedStructures: string[],
        selectedCustomization?: string
    ) => {
        startProcessing({
            selectedModel,
            targetImageSeriesUid: registrationTargetImage?.seriesId,
            selectedStructures,
            customizationName: selectedCustomization,
        });
    };

    return (
        <ContourPropagation
            availableModels={availableModels}
            onSubmit={onSubmit}
            isLoadingModels={isLoadingModels}
            targetImages={availableImages.map(normalizeBackendImage)}
            selectedTargetImage={registrationTargetImage}
            setSelectedTargetImage={setRegistrationTargetImage}
            isSubmitting={isSubmitting}
            error={error}
            showPseudonymization={true}
        />
    );
}
